import { useCallback, useMemo, useRef, useState } from 'react';
import WeatherMap from './WeatherMap';
import { findClosestPastLoadedFrame } from './mapUtils';
import MapRightControls from './MapRightControls';

import { LocationPopover } from './LocationPopover';
import TimerControl from './TimerControl';
import LoadingOverlay from './LoadingOverlay';
import { useTheme } from '@app/theme/ThemeContext';
import { useLayerManager } from './LayerManager';
import { useLocationPopover } from './hooks/useLocationPopover';
import { useToggleManager } from './hooks/useToggleManager';
import { useLegendTooltipControls } from './hooks/useLegendTooltipControls';
import { useUIStateManager } from './hooks/useUIStateManager';
import LocationSearch from './LocationSearch';
import { useMapState } from './MapStateProvider';

export interface MapWithControlsProps {
  readonly allowSwitchDataSource: boolean;
}

export default function MapWithControls({ allowSwitchDataSource }: MapWithControlsProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const {
    state: playState,
    datasource,
    map,
    currentFrame,
    cache,
    availableLayers,
    availableToggles,
    availableVariables,
    isDragging,
    switchDataSource,
    switchMap,
  } = useMapState();

  const controlsRightRef = useRef<HTMLDivElement>(null);

  // UI state management
  const {
    layersPanelVisible,
    setLayersPanelVisible,
    settingsPanelVisible,
    setSettingsPanelVisible,
    selectingDataSource,
    setSelectingDataSource,
  } = useUIStateManager();

  // Location popover state and logic
  const {
    locationPopoverVisible,
    locationPopoverCoords,
    showLocationPopover,
    hideLocationPopover,
  } = useLocationPopover();

  // Toggle state management
  const { activeToggleIds, handleToggleChange } = useToggleManager(availableToggles);

  // Global overlays opacity (Option A)
  const [globalOverlayOpacity, setGlobalOverlayOpacity] = useState<number>(0.8);

  // Find the best frame to display (current frame or closest past loaded frame)
  const displayFrame = findClosestPastLoadedFrame(currentFrame, cache);

  // Show loading indicator when initially loading data or when current frame is not yet loaded
  const isLoading =
    datasource == null ||
    cache == null ||
    currentFrame == null ||
    cache.frames[currentFrame].isDownloading;

  // Legend and tooltip controls
  const { tooltipControl, legendControlRefs } = useLegendTooltipControls({
    layers: availableLayers,
    toggles: availableToggles,
    activeToggleIds,
    frameCache: cache,
    displayFrame,
    locationPopoverVisible,
  });

  // Get filtered and rendered layers for display frame
  const effectiveLayers = useMemo(() => {
    return availableLayers.map((layer) => {
      const baseOpacity = layer.opacity ?? 1;
      const nextOpacity = Math.max(0, Math.min(1, baseOpacity * globalOverlayOpacity));
      return nextOpacity === baseOpacity ? layer : { ...layer, opacity: nextOpacity };
    });
  }, [availableLayers, globalOverlayOpacity]);

  const layers = useLayerManager({
    layers: effectiveLayers,
    toggles: availableToggles,
    activeToggleIds,
    frameCache: cache,
    displayFrame,
    isAnimationPlaying: playState === 'PLAYING',
    isDragging,
    isLoading,
  });

  const handleLocationSelect = useCallback(
    (lat: number, lon: number, name: string) => {
      showLocationPopover({
        coordinate: [lon, lat],
        name,
      });
      return false;
    },
    [showLocationPopover],
  );

  return (
    <div className="relative h-full w-full">
      <LoadingOverlay isLoading={isLoading} />

      {!isLoading && layers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-lg text-gray-500">No data available</div>
        </div>
      )}
      <WeatherMap
        layers={layers}
        tooltipControl={tooltipControl}
        onClick={showLocationPopover}
        selectedCoordinates={locationPopoverCoords}
      />

      {/* Map Search Control */}
      <LocationSearch onLocationSelect={handleLocationSelect} />

      {/* Legend Controls */}
      <div
        className={`absolute bottom-20 right-4 z-10 ${
          locationPopoverVisible ? 'opacity-0 pointer-events-none' : ''
        }`}
      >
        <div className="flex flex-col gap-2">
          {legendControlRefs
            .slice()
            .reverse()
            .map(({ id, ref }) => (
              <div
                key={id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-2"
              >
                <div ref={ref} className="jua-legend-host" />
              </div>
            ))}
        </div>
      </div>

      {/* Timer Control */}
      {cache && (
        <div
          className={`absolute bottom-0 left-0 right-0 ${locationPopoverVisible ? 'z-40' : 'z-10'}`}
        >
          <TimerControl />
        </div>
      )}
      {datasource && (
        <MapRightControls
          map={map}
          layersPanelVisible={layersPanelVisible}
          setLayersPanelVisible={setLayersPanelVisible}
          settingsPanelVisible={settingsPanelVisible}
          setSettingsPanelVisible={setSettingsPanelVisible}
          selectingDataSource={selectingDataSource}
          setSelectingDataSource={setSelectingDataSource}
          isDark={isDark}
          allowSwitchDataSource={allowSwitchDataSource}
          currentDataSource={datasource}
          currentToggles={availableToggles}
          availableVariables={availableVariables}
          activeToggleIds={activeToggleIds}
          onToggleChange={handleToggleChange}
          setCurrentDataSource={switchDataSource}
          setCurrentMap={switchMap}
          controlsRightRef={controlsRightRef}
          globalOverlayOpacity={globalOverlayOpacity}
          setGlobalOverlayOpacity={setGlobalOverlayOpacity}
        />
      )}

      {/* Location Popover */}
      {locationPopoverVisible && locationPopoverCoords && datasource && (
        <LocationPopover
          currentModel={datasource.model}
          coordinates={locationPopoverCoords}
          onClose={hideLocationPopover}
        />
      )}
    </div>
  );
}
